import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import api from "../lib/api";

/**
 * Email verification landing page — read `token` from query string, POST it
 * to /api/auth/verify-email, show result. Triggered when the user clicks the
 * verification link in their inbox.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("loading"); // loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setErrorMsg("No verification token in the link. Please use the link from your email.");
      return;
    }
    api
      .post(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(() => setState("success"))
      .catch((e) => {
        setState("error");
        setErrorMsg(e.response?.data?.detail || "Verification failed. The link may have expired.");
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-4" data-testid="verify-email-page">
      <div className="w-full max-w-md rounded-2xl bg-white border border-[#EAE6E1] p-8">
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] mb-2">
          UpTick
        </div>
        {state === "loading" && (
          <div data-testid="verify-email-loading" className="text-center py-6">
            <Loader2 className="w-10 h-10 text-[#547A8F] animate-spin mx-auto mb-3" strokeWidth={1.75} />
            <h1 className="font-heading text-xl text-[#2A2F2B] mb-1">Verifying your email...</h1>
            <p className="text-sm text-[#6B7068]">Just a moment.</p>
          </div>
        )}
        {state === "success" && (
          <div data-testid="verify-email-success" className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-[#5C8D7315] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#5C8D73]" strokeWidth={1.75} />
            </div>
            <h1 className="font-heading text-2xl text-[#2A2F2B] mb-1">Email verified</h1>
            <p className="text-sm text-[#6B7068] mb-5">
              You're all set. UpTick alerts, the daily digest, and password recovery emails will now reach you.
            </p>
            <Link
              to="/dashboard"
              data-testid="verify-email-go-dashboard"
              className="inline-block px-6 py-2.5 rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white text-sm font-medium transition-soft"
            >
              Open dashboard →
            </Link>
          </div>
        )}
        {state === "error" && (
          <div data-testid="verify-email-error" className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-[#C66B5915] flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-[#C66B59]" strokeWidth={1.75} />
            </div>
            <h1 className="font-heading text-2xl text-[#2A2F2B] mb-1">Couldn't verify</h1>
            <p className="text-sm text-[#6B7068] mb-5">{errorMsg}</p>
            <Link
              to="/dashboard"
              className="inline-block px-6 py-2.5 rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white text-sm font-medium transition-soft"
            >
              Back to dashboard
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
